import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project, ProjectsResponse, ProjectStatus } from '../types';
import projectService from '../services/projectService';
import { Button, Typography, Tooltip, Spin, Modal, message } from 'antd';
import { AppstoreOutlined, MenuOutlined, DeleteOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import CreateProjectDrawer from '../components/CreateProjectDrawer';
import { useAuth } from '../context/AuthContext';
import { canCreateProject, canDeleteProject } from '../utils/permissions';
import { extractErrorMessage } from '../utils/errorHandler';
import './Dashboard.css';

const { Title } = Typography;

// ==================== CONSTANTS ====================
const STATUSES = [
  ProjectStatus.PROCESSING_REQUIREMENT,
  ProjectStatus.GENERATING_ARTIFACTS,
  ProjectStatus.GENERATING_CODE,
  ProjectStatus.COMPLETED
];

const STATUS_CONFIG = {
  labels: {
    [ProjectStatus.PROCESSING_REQUIREMENT]: 'Processing Requirement',
    [ProjectStatus.GENERATING_ARTIFACTS]: 'Generating Artifacts',
    [ProjectStatus.GENERATING_CODE]: 'Generating Code',
    [ProjectStatus.VALIDATING]: 'Validating',
    [ProjectStatus.DEPLOYING]: 'Deploying',
    [ProjectStatus.COMPLETED]: 'Completed',
    [ProjectStatus.FAILED]: 'Failed'
  },
  icons: {
    [ProjectStatus.PROCESSING_REQUIREMENT]: '📋',
    [ProjectStatus.GENERATING_ARTIFACTS]: '🔧',
    [ProjectStatus.GENERATING_CODE]: '💻',
    [ProjectStatus.VALIDATING]: '✅',
    [ProjectStatus.DEPLOYING]: '🚀',
    [ProjectStatus.COMPLETED]: '✅',
    [ProjectStatus.FAILED]: '❌'
  },
  styles: {
    [ProjectStatus.PROCESSING_REQUIREMENT]: { background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' },
    [ProjectStatus.GENERATING_ARTIFACTS]: { background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' },
    [ProjectStatus.GENERATING_CODE]: { background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' },
    [ProjectStatus.VALIDATING]: { background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' },
    [ProjectStatus.DEPLOYING]: { background: 'rgba(236, 72, 153, 0.1)', color: '#ec4899' },
    [ProjectStatus.COMPLETED]: { background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' },
    [ProjectStatus.FAILED]: { background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }
  }
};

// ==================== DASHBOARD COMPONENT ====================
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  
  // ==================== STATE ====================
  const [projects, setProjects] = useState<Project[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<ProjectStatus | 'all'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);

  // ==================== EFFECTS ====================
  const fetchCalledRef = useRef(false);
  useEffect(() => {
    if (fetchCalledRef.current) return;
    fetchCalledRef.current = true;
    fetchProjects();
  }, []);

  // ==================== API FUNCTIONS ====================
  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const response: ProjectsResponse = await projectService.getAllProjects();
      if (response?.status === "success") {
        console.log("Fetched projects successfully:", response.data);
        setProjects(response?.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      const errorMessage = extractErrorMessage(error);
      message.error(errorMessage);
    } finally {
      setProjectsLoading(false);
    }
  };

  // ==================== COMPUTED VALUES ====================
  const groupedProjects = STATUSES.reduce((acc, status) => {
    acc[status] = projects.filter((project) => project.status === status);
    return acc;
  }, {} as Record<ProjectStatus, Project[]>);

  const displayProjects = projects.filter(project => {
    const isMainStatus = STATUSES.includes(project.status);
    if (selectedStatus === 'all') {
      return isMainStatus;
    }
    return project.status === selectedStatus;
  });

  // ==================== HELPER FUNCTIONS ====================
  const getBadgeStyles = (status: ProjectStatus) => {
    return STATUS_CONFIG.styles[status] || { background: 'rgba(107, 114, 128, 0.1)', color: '#6b7280' };
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handleCreateProject = () => {
    setDrawerOpen(true);
  };

  const handleProjectCreated = () => {
    fetchProjects();
    setDrawerOpen(false);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      const errorMessage = extractErrorMessage(error);
      message.error(errorMessage);
    }
  };

  const handleDeleteProject = (projectId: string, projectName: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click navigation
    
    Modal.confirm({
      title: 'Delete Project',
      content: (
        <div>
          <p>Are you sure you want to delete the project <strong>"{projectName}"</strong>?</p>
          <p style={{ color: '#ff4d4f', fontSize: '12px' }}>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setDeletingProjectId(projectId);
          await projectService.deleteProject(projectId);
          message.success('Project deleted successfully');
          // Remove the project from the local state to update UI immediately
          setProjects(prevProjects => prevProjects.filter(project => project.id !== projectId));
        } catch (error) {
          console.error('Failed to delete project:', error);
          const errorMessage = extractErrorMessage(error);
          message.error(errorMessage);
        } finally {
          setDeletingProjectId(null);
        }
      }
    });
  };

  // ==================== STYLES ====================
  const styles = {
    app: {
      display: 'flex',
      minHeight: '100vh',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: '#f8f9fa'
    },
    sidebar: {
      width: '280px',
      background: '#ffffff',
      borderRight: '1px solid #e5e7eb',
      position: 'fixed' as const,
      height: '100vh',
      left: 0,
      top: 0,
      zIndex: 100,
      boxShadow: '2px 0 10px rgba(0, 0, 0, 0.05)'
    },
    sidebarHeader: {
      padding: '15px 25px',
      borderBottom: '1px solid #e5e7eb',
      background: '#0e1319cc'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    brandName: {
      color: '#ffffff',
      fontSize: '16px',
      fontWeight: 600,
      letterSpacing: '0.5px',
      margin: 0
    },
    mainContent: {
      flex: 1,
      marginLeft: '280px',
      display: 'flex',
      flexDirection: 'column' as const
    },
    topBar: {
      background: '#0e1319cc',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      padding: '5px 30px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky' as const,
      top: 0,
      zIndex: 50,
      backdropFilter: 'blur(10px)'
    },
    contentArea: {
      flex: 1,
      padding: '30px',
      overflowY: 'auto' as const
    },
    projectsSection: {
      background: '#ffffff',
      borderRadius: '8px',
      padding: '30px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    }
  };

  return (
    <div style={styles.app}>
      {/* Left Navigation Panel */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <div style={styles.logo}>
            <h2 style={styles.brandName}>R2C WORKBENCH</h2>
          </div>
        </div>
        
        {/* Scrollable Content Area with padding for user profile */}
        <div style={{ 
          padding: '20px 0',
          overflowY: 'auto',
          height: 'calc(100vh - 200px)', // Subtract header (~70px) and user profile (~130px)
          paddingBottom: '30px'
        }}>
          {/* Project Stages Section */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '12px', 
              textTransform: 'uppercase', 
              letterSpacing: '1px', 
              marginBottom: '15px', 
              padding: '0 25px', 
              fontWeight: 600 
            }}>
              Project Stages
            </h3>
            
            {/* All Projects */}
            <div
              className={`nav-item${selectedStatus === 'all' ? ' active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedStatus('all')}
            >
              <span className="nav-icon">📊</span>
              <span className="nav-label">All Projects</span>
              <span className="nav-badge">
                {projects.filter(project => STATUSES.includes(project.status)).length}
              </span>
            </div>

            {/* Individual Status Items */}
            {STATUSES.map((status: ProjectStatus) => (
              <div
                key={status}
                className={`nav-item${selectedStatus === status ? ' active' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedStatus(status)}
              >
                <span className="nav-icon">{STATUS_CONFIG.icons[status]}</span>
                <span className="nav-label">{STATUS_CONFIG.labels[status]}</span>
                <span className="nav-badge">
                  {groupedProjects[status].length}
                </span>
              </div>
            ))}
          </div>

          {/* Quick Actions Section */}
          <div style={{ 
            marginTop: '20px',
            paddingTop: '20px',
            paddingBottom: '10px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '12px', 
              textTransform: 'uppercase', 
              letterSpacing: '1px', 
              marginBottom: '15px', 
              padding: '0 25px', 
              fontWeight: 600 
            }}>
              Quick Actions
            </h3>
            <div
              className={`nav-item quick-action${viewMode === 'list' ? ' active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
            >
              <span className="nav-icon">{viewMode === 'list' ? '⊞' : '☰'}</span>
              <span className="nav-label">{viewMode === 'list' ? 'Grid View' : 'List View'}</span>
            </div>
          </div>
        </div>

        {/* User Profile Section at Bottom */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          borderTop: '1px solid #e5e7eb',
          background: '#ffffff',
          zIndex: 10,
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)'
        }}>
          {/* User Info */}
          <div style={{
            padding: '15px 25px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 600
            }}>
              <UserOutlined />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                color: '#111827',
                fontSize: '14px',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {user?.name || user?.username || 'Guest User'}
              </div>
              <div style={{
                color: '#6b7280',
                fontSize: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {user?.email || user?.userPrincipalName || 'guest@local'}
              </div>
            </div>
          </div>
          
          {/* Logout Button */}
          <div
            className="nav-item"
            role="button"
            tabIndex={0}
            onClick={handleLogout}
            style={{
              borderRadius: 0,
              color: '#ef4444',
              cursor: 'pointer'
            }}
          >
            <span className="nav-icon"><LogoutOutlined /></span>
            <span className="nav-label">Logout</span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={styles.mainContent}>
        {/* Top Bar */}
        <div style={styles.topBar}>
          <div>
            <h2 style={{ 
              color: '#ffffff', 
              fontSize: '16px', 
              fontWeight: 600, 
              margin: 0, 
              marginBottom: '2px' 
            }}>
              Projects Dashboard
            </h2>
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontSize: '12px', 
              margin: 0 
            }}>
              Manage and track your project progress
            </p>
          </div>
          {canCreateProject(user) && (
            <Button 
              type="primary" 
              onClick={() => setDrawerOpen(true)}
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)'
              }}
            >
              Create Project
            </Button>
          )}
        </div>

        {/* Content Area */}
        <div style={styles.contentArea}>
          {projectsLoading ? (
            <Spin spinning style={{ 
              width: '100%', 
              minHeight: 200, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }} />
          ) : displayProjects.length === 0 ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              minHeight: 400 
            }}>
              <Title level={4} style={{ marginTop: 24, textAlign: 'center' }}>
                {projects.length === 0 
                  ? <>Welcome to R2C WorkBench,<br />Create a project to get started.</>
                  : "No Data"
                }
              </Title>
            </div>
          ) : (
            <div style={styles.projectsSection}>
              {/* Projects Header */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '25px' 
              }}>
                <h2 style={{ 
                  color: '#111827', 
                  fontSize: '20px', 
                  fontWeight: 700, 
                  margin: 0 
                }}>
                  Projects
                </h2>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  fontSize: '14px' 
                }}>
                  <span style={{ fontWeight: 500, color: '#d32f2f' }}>
                    {selectedStatus === 'all' ? 'All Projects' : STATUS_CONFIG.labels[selectedStatus as ProjectStatus]}
                  </span>
                  <span style={{ color: '#cbd5e0' }}>•</span>
                  <span style={{ color: '#6b7280' }}>
                    {displayProjects.length} {displayProjects.length === 1 ? 'project' : 'projects'}
                  </span>
                </div>
              </div>

              {/* Projects Display */}
              {viewMode === 'grid' ? (
                // Grid View
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                  gap: '20px' 
                }}>
                  {displayProjects.map((project) => (
                    <div
                      key={project.id}
                      className="project-card"
                      style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '20px',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => navigate(`/projects/${project.id}`)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-3px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.borderColor = '#d32f2f';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.05)';
                      }}
                    >
                    {/* Card Header */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      marginBottom: '15px' 
                    }}>
                      <div style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        ...getBadgeStyles(project.status)
                      }}>
                        {STATUS_CONFIG.labels[project.status]}
                      </div>
                      {canDeleteProject(user) && (
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          size="small"
                          loading={deletingProjectId === project.id}
                          onClick={(e) => handleDeleteProject(project.id, project.name, e)}
                          className="project-delete-btn"
                          style={{
                            color: '#ff4d4f',
                            border: 'none',
                            boxShadow: 'none',
                            padding: '4px',
                            minWidth: 'auto',
                            height: 'auto'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 77, 79, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        />
                      )}
                    </div>

                    {/* Card Content */}
                    <div style={{ marginBottom: '20px' }}>
                      <Tooltip title={project.name} placement="top">
                        <h3 style={{
                          color: '#111827',
                          fontSize: '16px',
                          fontWeight: 600,
                          marginBottom: '8px',
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {project.name}
                        </h3>
                      </Tooltip>
                      <Tooltip title={project.description} placement="bottom">
                        <p style={{
                          color: '#6b7280',
                          fontSize: '13px',
                          lineHeight: 1.5,
                          marginBottom: '12px',
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                          overflow: 'hidden'
                        }}>
                          {project.description}
                        </p>
                      </Tooltip>
                      <a
                        href={`/projects/${project.id}`}
                        style={{
                          color: '#d32f2f',
                          textDecoration: 'none',
                          fontSize: '13px',
                          fontWeight: 500
                        }}
                        onClick={(e) => e.preventDefault()}
                      >
                        View Details
                      </a>
                    </div>
                  </div>
                ))}
                </div>
              ) : (
                // List View
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '10px' 
                }}>
                  {displayProjects.map((project) => (
                    <div
                      key={project.id}
                      className="project-card"
                      style={{
                        background: 'white',
                        borderRadius: '8px',
                        padding: '16px 20px',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                      onClick={() => navigate(`/projects/${project.id}`)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.borderColor = '#d32f2f';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.05)';
                      }}
                    >
                      {/* Left Section - Project Info */}
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <div style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginRight: '16px',
                          minWidth: '120px',
                          textAlign: 'center',
                          ...getBadgeStyles(project.status)
                        }}>
                          {STATUS_CONFIG.labels[project.status]}
                        </div>
                        <div style={{ flex: 1 }}>
                          <Tooltip title={project.name} placement="top">
                            <h3 style={{
                              color: '#111827',
                              fontSize: '14px',
                              fontWeight: 600,
                              marginBottom: '4px',
                              lineHeight: 1.3
                            }}>
                              {project.name}
                            </h3>
                          </Tooltip>
                          <Tooltip title={project.description} placement="bottom">
                            <p style={{
                              color: '#6b7280',
                              fontSize: '12px',
                              lineHeight: 1.4,
                              margin: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '400px'
                            }}>
                              {project.description}
                            </p>
                          </Tooltip>
                        </div>
                      </div>
                      
                      {/* Right Section - Actions */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                          color: '#6b7280',
                          fontSize: '12px'
                        }}>
                          {new Date(project.createdAt).toLocaleDateString()}
                        </span>
                        <div style={{ 
                          color: '#d32f2f', 
                          fontSize: '12px',
                          fontWeight: 500
                        }}>
                          View Details →
                        </div>
                        {canDeleteProject(user) && (
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            size="small"
                            loading={deletingProjectId === project.id}
                            onClick={(e) => handleDeleteProject(project.id, project.name, e)}
                            className="project-delete-btn"
                            style={{
                              color: '#ff4d4f',
                              border: 'none',
                              boxShadow: 'none',
                              padding: '4px',
                              minWidth: 'auto',
                              height: 'auto'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 77, 79, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Project Drawer */}
      <CreateProjectDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSuccess={(projectId: string) => {
          setDrawerOpen(false);
          navigate(`/projects/${projectId}`);
        }}
      />
    </div>
  );
};

export default Dashboard;